/*
 * Developed for G.J. Gardner Homes by Softeq Development Corporation
 * http://www.softeq.com
 */

import { Component, OnInit } from '@angular/core';
import { ActivatedRoute } from '@angular/router';

import {
  BuilderNavigationService,
  CommonActions,
  CommonFlowState,
  ListActionPipelines,
} from '@gh/builder-shared';
import {
  AelEvents,
  BuilderPermissionSet,
  getAelEventsArray,
  MilestoneEventFromToType,
} from '@gh/builder-shared-data';
import { AppRouter, EnumDictionary, getRouteInitData, UserContext } from '@gh/common-shared';
import { SlicedDataSource } from '@gh/core-data';
import { TranslationService } from '@gh/core-mls';
import { GridDefinitionFactory, LayoutBasedGridDefinition } from '@gh/core-ui-grid';
import type { ValueFormatterParams } from 'ag-grid-community';
import { Observable } from 'rxjs';
import { MilestoneEventListBusinessService } from '../../resources/milestone-event-list-business.service';
import { MilestoneEventListItem } from '../../resources/milestone-event-list.model';

@Component({
  standalone: false,
  selector: 'gh-milestone-events-list',
  templateUrl: 'milestone-event-list.component.html',
})
export class MilestoneEventListComponent implements OnInit {

  gridDefinition: LayoutBasedGridDefinition;
  milestoneEventFromToType: EnumDictionary<MilestoneEventFromToType>;
  dataSource: SlicedDataSource<MilestoneEventListItem>;

  constructor(private business: MilestoneEventListBusinessService,
              private userContext: UserContext,
              private route: ActivatedRoute,
              private router: AppRouter,
              private gridDefinitionFactory: GridDefinitionFactory,
              private translationService: TranslationService,
              private pipelines: ListActionPipelines,
              private commonActions: CommonActions,
              private navigationService: BuilderNavigationService) {
  }

  ngOnInit(): void {
    const { gridLayout, dataSource, milestoneEventFromToType } = getRouteInitData(this.route);
    this.milestoneEventFromToType = milestoneEventFromToType;
    this.dataSource = dataSource;
    this.gridDefinition = this.buildGridDefinition();
    this.gridDefinition.bind(gridLayout);
  }

  onCreate(): void {
    this.router.navigateToChild(['/setup/milestone-events/new']);
  }

  onOpen({ milestoneEventId }: MilestoneEventListItem): void {
    if (this.userContext.hasPermission(BuilderPermissionSet.Setup.MilestoneEvents.EDIT_VIEW)) {
      this.router.navigateToChild(['/setup/milestone-events', milestoneEventId]);
    }
  }

  onDelete(milestoneEvent: MilestoneEventListItem): void {
    const flow = this.pipelines.toggleItem({
      question: 'msg_milestone_event_delete',
      execute: this.commonActions.execute((state: CommonFlowState<any>): Observable<void> =>
        this.business.remove(state.value)),
      finish: () => this.loadData(),
    });

    flow.run({
      value: milestoneEvent,
    });
  }

  goToAel(event: MilestoneEventListItem): void {
    this.navigationService.goToAel({
      entityType: 'MILESTONE_EVENT',
      entityId: event && event.milestoneEventId || void 0,
      eventTypeIds: getAelEventsArray(AelEvents.MilestoneEvent),
      label: 'lbl_milestone_events',
    });
  }

  private buildGridDefinition(): LayoutBasedGridDefinition {
    return this.gridDefinitionFactory.buildLayoutBased({
      sendFrom: {
        valueFormatter: (params: ValueFormatterParams) =>
          params.value || this.milestoneEventFromToType.get(params.data.sendFromValue),
      },
      sendTo: {
        valueFormatter: (params: ValueFormatterParams) =>
          params.value || this.milestoneEventFromToType.get(params.data.sendToValue),
      },
    },                                                 {
      defaultColDef: {
        comparator: () => 0,
      },
      getRowId: (params) => String(params.data.milestoneEventId),
    });
  }

  private loadData(): void {
    this.dataSource = this.business.findAllAsDataSource();
  }
}
